import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  XMarkIcon,
  UserCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import apiClient from '../../lib/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchTask {
  id: string;
  user_id: string;
  company: string;
  type: string;
  status: string;
  progress: number;
  credits_used: number;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  search_titles?: string | null;
  user?: { id: string; name: string; email: string };
}

interface Lead {
  id: number;
  task_id: string;
  name: string;
  title: string;
  company_name: string;
  company_domain: string | null;
  company_type: string | null;
  company_address: string | null;
  location: string | null;
  created_at: string;
}

interface Pagination {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['', 'pending', 'processing', 'retrieving', 'completed', 'failed', 'paused', 'cancelled'];

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
    failed:     'bg-red-50 text-red-700 border border-red-200',
    processing: 'bg-brand-50 text-brand-700 border border-brand-200',
    pending:    'bg-slate-100 text-slate-500 border border-slate-200',
    retrieving: 'bg-blue-50 text-blue-700 border border-blue-200',
    extracting: 'bg-amber-50 text-amber-700 border border-amber-200',
    paused:     'bg-amber-50 text-amber-700 border border-amber-200',
    cancelled:  'bg-slate-100 text-slate-500 border border-slate-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${map[status] ?? 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
      {status}
    </span>
  );
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function parseTitles(raw?: string | null): string[] {
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return [raw]; }
}

function formatDuration(startedAt?: string | null, completedAt?: string | null): string | null {
  if (!startedAt || !completedAt) return null;
  const diffMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (diffMs < 0) return null;
  const s = Math.round(diffMs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

// ─── Results Drawer ───────────────────────────────────────────────────────────

interface ResultsDrawerProps {
  task: SearchTask;
  onClose: () => void;
}

function ResultsDrawer({ task, onClose }: ResultsDrawerProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    apiClient.get(`/admin/searches/${task.id}/results`, { params })
      .then(({ data }) => {
        setLeads(data.leads?.data ?? []);
        const pg = data.leads;
        setPagination({ total: pg.total, current_page: pg.current_page, per_page: pg.per_page, last_page: pg.last_page });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [task.id, page, perPage, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
      const url = `${baseUrl}/admin/searches/${task.id}/export`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        ?? `results_${task.id}.csv`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExporting(false);
    }
  };

  const totalLeads = pagination?.total ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="w-full max-w-4xl bg-white h-full flex flex-col shadow-2xl overflow-hidden">

        {/* Drawer Header */}
        <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-4 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-[18px] font-extrabold text-slate-900 tracking-tight truncate">{task.company}</h2>
              <StatusBadge status={task.status} />
              <span className="text-[11px] text-slate-400 font-medium capitalize">{task.type}</span>
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-[12px] text-slate-500 flex-wrap">
              {task.user && (
                <span>
                  by <span className="font-semibold text-slate-700">{task.user.name}</span>
                  <span className="text-slate-400 ml-1">({task.user.email})</span>
                </span>
              )}
              <span>{new Date(task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              <span className="font-semibold text-slate-700">{task.credits_used} credits used</span>
              {formatDuration(task.started_at, task.completed_at) && (
                <span className="font-semibold text-slate-700">
                  ⏱ {formatDuration(task.started_at, task.completed_at)}
                </span>
              )}
            </div>
            {parseTitles(task.search_titles).length > 0 && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Roles searched:</span>
                {parseTitles(task.search_titles).map((t, i) => (
                  <span key={i} className="text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleExport}
              disabled={exporting || totalLeads === 0}
              className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white px-3.5 py-2 rounded-xl text-[12px] font-bold transition-colors shadow-sm shadow-brand-500/20"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {exporting ? 'Exporting…' : `Export CSV${totalLeads ? ` (${totalLeads})` : ''}`}
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3.5 border-b border-slate-100 flex items-center justify-between gap-4 shrink-0 bg-slate-50/50">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Filter by name, title, company…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2 bg-white border border-slate-200 text-[12px] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 w-64"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[12px] text-slate-400">
              {loading ? '…' : `${totalLeads.toLocaleString()} leads`}
            </span>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="px-3 py-2 bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 rounded-xl focus:outline-none"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {leads.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
              <UserCircleIcon className="w-12 h-12 opacity-30" />
              <p className="text-[14px] font-semibold">No leads found{search ? ' for this filter' : ''}</p>
              {task.status !== 'completed' && (
                <p className="text-[12px]">
                  {task.status === 'cancelled'
                    ? 'This search was cancelled — no results were saved before it stopped.'
                    : task.status === 'paused'
                      ? 'This search is paused — showing results gathered so far.'
                      : `This search is ${task.status} — results will appear when complete.`}
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[600px]">
              <thead className="sticky top-0 bg-white border-b border-slate-100 z-10">
                <tr className="text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em]">
                  <th className="px-5 py-3.5 w-8">#</th>
                  <th className="px-5 py-3.5">Name & Title</th>
                  <th className="px-5 py-3.5">Company</th>
                  <th className="px-5 py-3.5">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading
                  ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {[12, 28, 20, 16].map((w, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-slate-100 animate-pulse rounded" style={{ width: `${w * 4}px` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                  : leads.map((lead, idx) => {
                    const rowNum = (pagination ? (pagination.current_page - 1) * pagination.per_page : 0) + idx + 1;
                    return (
                      <tr key={lead.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5 text-[11px] text-slate-400 font-medium">{rowNum}</td>
                        <td className="px-5 py-3.5">
                          <p className="text-[13px] font-bold text-slate-900 leading-tight">{lead.name}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">{lead.title || '—'}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-[12px] font-semibold text-slate-700">{lead.company_name}</p>
                          {lead.company_domain && (
                            <p className="text-[10px] text-slate-400 mt-0.5">{lead.company_domain}</p>
                          )}
                          {lead.company_type && (
                            <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-widest text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded">
                              {lead.company_type}
                            </span>
                          )}
                          {lead.company_address && (
                            <p className="text-[10px] text-slate-400 mt-0.5">{lead.company_address}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-[12px] text-slate-500">{lead.location ?? '—'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Drawer Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
            <span className="text-[12px] text-slate-400">
              Showing <span className="font-bold text-slate-600">
                {(pagination.current_page - 1) * pagination.per_page + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)}
              </span> of <span className="font-bold text-slate-600">{pagination.total.toLocaleString()}</span>
            </span>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(pagination.last_page, 7) }, (_, i) => {
                const p = i + 1;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 text-[12px] font-bold rounded-lg transition-colors ${
                      pagination.current_page === p
                        ? 'bg-brand-500 text-white'
                        : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              {pagination.last_page > 7 && (
                <>
                  <span className="text-slate-400 text-[12px] px-1">…</span>
                  <button
                    onClick={() => setPage(pagination.last_page)}
                    className={`w-8 h-8 text-[12px] font-bold rounded-lg transition-colors ${
                      pagination.current_page === pagination.last_page
                        ? 'bg-brand-500 text-white'
                        : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {pagination.last_page}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const copyToClipboard = (text: string, label: string = 'ID') => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`✓ ${label} copied`);
  }).catch(() => {
    toast.error('Failed to copy');
  });
};

export default function SearchHistory() {
  const [searches, setSearches] = useState<SearchTask[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [selectedTask, setSelectedTask] = useState<SearchTask | null>(null);

  const fetchSearches = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    if (status) params.status = status;

    apiClient.get('/admin/searches', { params })
      .then(({ data }) => {
        setSearches(data.data ?? []);
        setPagination({
          total: data.total,
          current_page: data.current_page,
          per_page: data.per_page,
          last_page: data.last_page,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, perPage, search, status]);

  useEffect(() => { fetchSearches(); }, [fetchSearches]);

  const handleExportRow = async (task: SearchTask, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = localStorage.getItem('admin_token');
    const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
    const url = `${baseUrl}/admin/searches/${task.id}/export`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await response.blob();
    const filename = `results_${task.company.replace(/\s+/g, '_')}.csv`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const totalPages = pagination?.last_page ?? 1;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">Search History</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            {pagination ? `${pagination.total.toLocaleString()} total searches` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <FunnelIcon className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={status}
              onChange={e => { setStatus(e.target.value); setPage(1); }}
              className="pl-8 pr-3 py-2 bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 appearance-none cursor-pointer"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All Statuses'}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search company, user, or job ID…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2 bg-white border border-slate-200 text-[13px] placeholder:text-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 w-44 sm:w-56"
            />
          </div>
          <select
            value={perPage}
            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="px-3 py-2 bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 rounded-xl focus:outline-none"
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[900px]">
          <thead>
            <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
              <th className="px-5 py-4">Company</th>
              <th className="px-5 py-4">Job ID</th>
              <th className="px-5 py-4">User</th>
              <th className="px-5 py-4">Roles</th>
              <th className="px-5 py-4">Type</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Duration</th>
              <th className="px-5 py-4">Credits</th>
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading
              ? Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 10 }).map((__, j) => (
                    <td key={j} className="px-5 py-4">
                      <div className="h-4 bg-slate-100 animate-pulse rounded" style={{ width: `${40 + (j % 4) * 15}%` }} />
                    </td>
                  ))}
                </tr>
              ))
              : searches.length === 0
                ? (
                  <tr>
                    <td colSpan={10} className="px-5 py-14 text-center text-slate-400 text-[13px]">No searches found.</td>
                  </tr>
                )
                : searches.map(s => (
                  <tr
                    key={s.id}
                    className="hover:bg-slate-50/60 transition-colors group cursor-pointer"
                    onClick={() => setSelectedTask(s)}
                  >
                    <td className="px-5 py-3.5">
                      <p className="text-[13px] font-bold text-slate-900">{s.company}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <code className="text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded font-mono">{s.id.substring(0, 8)}…</code>
                        <button
                          onClick={e => { e.stopPropagation(); copyToClipboard(s.id, 'Job ID'); }}
                          className="text-slate-400 hover:text-slate-700 transition-colors"
                          title="Copy Job ID"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {s.user ? (
                        <Link
                          to={`/users/${s.user.id}`}
                          onClick={e => e.stopPropagation()}
                          className="hover:text-brand-600 transition-colors"
                        >
                          <p className="text-[12px] font-semibold text-slate-700">{s.user.name}</p>
                          <p className="text-[10px] text-slate-400">{s.user.email}</p>
                        </Link>
                      ) : (
                        <span className="text-slate-300 text-[12px]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex flex-wrap gap-1 max-w-[160px]">
                        {parseTitles(s.search_titles).slice(0, 3).map((t, i) => (
                          <span key={i} className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full whitespace-nowrap">{t}</span>
                        ))}
                        {parseTitles(s.search_titles).length > 3 && (
                          <span className="text-[9px] text-slate-400">+{parseTitles(s.search_titles).length - 3}</span>
                        )}
                        {parseTitles(s.search_titles).length === 0 && <span className="text-slate-300 text-[11px]">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[12px] text-slate-500 capitalize">{s.type}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
                    <td className="px-5 py-3.5 text-[11px] text-slate-500 whitespace-nowrap">
                      {formatDuration(s.started_at, s.completed_at) ?? '—'}
                    </td>
                    <td className="px-5 py-3.5 text-[13px] font-bold text-slate-700">{s.credits_used}</td>
                    <td className="px-5 py-3.5 text-[11px] text-slate-500">
                      {new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center gap-1.5 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={e => { e.stopPropagation(); setSelectedTask(s); }}
                          className="flex items-center gap-1.5 text-[11px] font-bold text-brand-600 hover:text-slate-700 bg-brand-50 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg transition-colors"
                        >
                          <EyeIcon className="w-3.5 h-3.5" /> View
                        </button>
                        {s.status === 'completed' && (
                          <button
                            onClick={e => handleExportRow(s, e)}
                            className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg transition-colors"
                          >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" /> CSV
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50/40">
            <span className="text-[12px] text-slate-400">
              Page <span className="font-bold text-slate-700">{pagination.current_page}</span> of{' '}
              <span className="font-bold text-slate-700">{totalPages}</span>
              {' '}— <span className="font-bold text-slate-700">{pagination.total.toLocaleString()}</span> results
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const p = i + 1;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 text-[12px] font-bold rounded-lg transition-colors ${
                      pagination.current_page === p
                        ? 'bg-brand-500 text-white'
                        : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              {totalPages > 7 && (
                <>
                  <span className="text-slate-400 px-1">…</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className={`w-8 h-8 text-[12px] font-bold rounded-lg transition-colors ${
                      pagination.current_page === totalPages ? 'bg-brand-500 text-white' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={pagination.current_page === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Drawer */}
      {selectedTask && (
        <ResultsDrawer task={selectedTask} onClose={() => setSelectedTask(null)} />
      )}
    </div>
  );
}
